<script>
import Datamatrix from '@/components/dev/Datamatrix.vue';
import { mapGetters } from 'vuex/dist/vuex.common.js';
import LabelArchiveContentsBadge from '../../LabelArchiveContentsBadge.vue';

export default {
  name: 'CockpitMatryoshkaInheritanceTreeSectonItem',
  props: {
    item: Object,
    levelIcon: String,
    isSelected: Boolean,
    canSelect: Boolean,
    canRemoveFromParent: Boolean,
    rootLabelArchiveId: Number,
    noOperations: Boolean,
  },
  data: () => ({
    selectedAnimation: false,
    unselectedAnimation: false,
  }),
  components: { Datamatrix, LabelArchiveContentsBadge },
  emits: [
    'disconnect',
    'select-item-id',
    'go-to-serial-number',
    'utilize-empty',
  ],
  computed: {
    ...mapGetters('emptyLabelArchive', [
      'isLabelArchiveEmpty',
    ]),
    ...mapGetters('debug', ['debug']),
    notLockedRelations() {
      return this.item.containedLabels.filter(r => !r.locked);
    },
    notEmptyRelations() {
      return this.item.containedLabels.filter(r => !this.isLabelArchiveEmpty(r.id));
    },
    canUnlink() {
      return this.notLockedRelations.length === 0 || this.canRemoveFromParent;
    },
    rootRelation() {
      const parentRelation = (this.item?.containedLabels || []).find(r => r.id === this.rootLabelArchiveId);
      const childrenRelation = (this.item?.containers || []).find(r => r.containerId === this.rootLabelArchiveId);

      return parentRelation || childrenRelation || {};
    },
    isLocked() {
      return this.rootRelation?.locked;
    },
    isEmpty() {
      return this.isLabelArchiveEmpty(this.item.id);
    },
    isNotEmptyStatus() {
      if (this.isLabelArchiveEmpty(this.item.id)) return true;
      return this.item.labelStatus !== 'Empty';
    },
  },
  watch: {
    isSelected(newVal) {
      this.selectedAnimation = newVal;
      this.unselectedAnimation = !newVal;
    },
  },
  methods: {
    async confirm() {
      const value = await this.$bvModal.msgBoxConfirm('Jesteś pewny?', {
        size: 'sm',
        buttonSize: 'md',
        okVariant: 'success',
        okTitle: 'Tak',
        cancelTitle: 'Nie',
        centered: true,
      });

      return value;
    },
    async confirmUtilizeEmpty() {
      const value = await this.$bvModal.msgBoxConfirm(`Usunięcie wolnego miejsca będzie
      skutkowało przesunięciem kolejki agregacyjnej.`, {
        title: 'UWAGA!',
        size: 'md',
        buttonSize: 'md',
        okVariant: 'success',
        okTitle: 'Usuń wolne miejsce',
        cancelTitle: 'Anuluj',
        centered: true,
      });

      return value;
    },
    async handleDisconnect() {
      const value = await this.confirm();
      if (value) {
        this.$emit('disconnect');
      }
    },
    async handleUtilizeEmpty() {
      const value = await this.confirmUtilizeEmpty();
      if (value) {
        this.$emit('utilize-empty');
      }
    },
  },
};
</script>

<template>
  <div v-if="isNotEmptyStatus">
    <div
      v-if="isEmpty"
      class="tree-section-item"
    >
      <div class="level-icon">
        <i :class="levelIcon" />
      </div>

      <div class="inner">
        <div class="title d-flex align-items-center w-100">
          <Tippy
            v-if="isLocked"
            arrow
            placement="bottom"
            class="mr-3"
          >
            <template #trigger>
              <div class="locked-info">
                <i class="fas fa-hand-holding-box" />
              </div>
            </template>

            <div>
              Z tego miejsca element został odłączony operacją manualną
            </div>
          </Tippy>

          <div class="small text-muted">
            Wolne miejsce
          </div>

          <div
            class="unlink-button ml-auto"
            :class="{
              'disabled': !canUnlink
            }"
            @click="handleUtilizeEmpty()"
          >
            <i class="fas fa-trash" />
            Usuń wolne miejsce
          </div>

          <i
            v-if="canSelect"
            class="far fa-square opacity-0 mr-2 h6 mb-0"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="tree-section-item"
      :class="{
        'selected': isSelected,
        'selected-animation': selectedAnimation,
        'unselected-animation': unselectedAnimation,
      }"
    >
      <div class="level-icon">
        <i :class="levelIcon" />
      </div>

      <div class="inner">
        <div class="title d-flex align-items-center">
          <i
            v-if="canSelect && !noOperations"
            class="mr-2 h6 mb-0"
            :class="{
              'far fa-check-square cursor-pointer': isSelected,
              'far fa-square cursor-pointer': !isSelected,
            }"
            @click.stop.prevent="$emit('select-item-id', item.id)"
          />

          <Datamatrix
            v-if="item.scanCode"
            :encode-string="item.scanCode"
            width="35px"
            height="35px"
            no-tooltip
          />

          {{ item.serialNumber }}
        </div>

        <div class="ml-auto" />

        <LabelArchiveContentsBadge
          :label-archive="item"
          class="mr-3"
        />

        <Tippy
          v-if="isLocked"
          arrow
          placement="bottom"
          class="mr-3"
        >
          <template #trigger>
            <div class="locked-info">
              <i class="fas fa-lock" />
            </div>
          </template>

          <div>
            Relacja tych elemetów jest zablokowana i nie będzie zmieniona
            przez automatyczną agregację
          </div>
        </Tippy>

        <div
          v-if="!noOperations"
          v-tippy
          class="unlink-button"
          :class="{
            'disabled': !canUnlink
          }"
          :title="canUnlink ? 'Odłącz element' : 'Nie można odłączyć elementu, który ma przypisane elementy podrzędne'"
          @click="canUnlink ? handleDisconnect() : undefined"
        >
          <i class="fas fa-unlink" />
          Odłącz
        </div>

        <div
          class="in-button"
          @click.stop.prevent="$emit('go-to-serial-number', item)"
        >
          <i class="fas fa-chevron-right" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@keyframes selected {
  0% {
    background-color: transparent;
  }

  50% {
    background-color: #76b033;
  }

  100% {
    background-color: #ececec;
  }
}

@keyframes unselected {
  0% {
    background-color: #ececec;
  }

  50% {
    opacity: 0.4;
    background-color: #ffffff;
  }

  100% {
    background-color: transparent;
  }
}

.tree-section-item {
  position: relative;

  &.selected-animation, &.selected {
    .inner {
      animation: selected 1.5s;
      background-color: #ececec;
    }
  }

  &.unselected-animation {
    .inner {
      animation: unselected 1.5s;
    }
  }

  .level-icon {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 16px;
    color: #B0B0B0;
  }

  .inner {
    width: 100%;
    height: 54px;
    border: 1px solid #F0F1F3;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: black;
    display: flex;
    align-items: center;
    padding: 12px 22px;
    margin-left: 30px;

    .locked-info {
      opacity: 0.5;
    }

    .unlink-button {
      margin-left: 0.5rem;
      padding: 2px 22px;
      font-size: 12px;
      border-radius: 50px;
      background-color: #EFEFEF;
      color: #7F7F7F;
      cursor: pointer;

      &.disabled {
        cursor: not-allowed;
        background-color: #D9D9D9;
      }

      &:hover {
        background-color: #D9D9D9;
      }
    }

    .in-button {
      margin-left: 12px;
      color: 7E7E7E;
      font-size: 14px;
      line-height: 0;
      padding: 2px 10px;
      cursor: pointer;
      border-radius: 40px;

      i {
        transform: translateY(1.5px);
      }

      &:hover {
        background-color: #F0F1F3;
      }
    }
  }
}
</style>
